import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import {
  canVerifyTeamScores,
  loadTournamentVerification,
} from "../utility/loadTournamentVerification";

/**
 * Tournament verification flags and states.
 * Reads get_tournament_verification_bundle (tables + existing RPCs), not an edge function.
 */
export const useTournamentVerification = (tournamentId) => {
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVerificationStatus = useCallback(async () => {
    if (!tournamentId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await loadTournamentVerification(supabase, tournamentId);
      setVerificationStatus(data);
    } catch (err) {
      console.error("Error fetching verification status:", err);
      setError("Failed to fetch verification status");
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  // Initial fetch
  useEffect(() => {
    fetchVerificationStatus();
  }, [fetchVerificationStatus]);

  // Note: Real-time subscriptions removed for team verification
  // Team verification is user-initiated and doesn't need real-time updates
  // The UI updates optimistically when users click verify/unverify

  // Helper functions for components
  const canVerifyTeam = useCallback(
    (teamId) => canVerifyTeamScores(verificationStatus, teamId),
    [verificationStatus]
  );

  // Check if a team has all required scores entered
  const teamHasAllScores = useCallback(
    async (teamId) => {
      if (!teamId) return false;

      try {
        // Get the team's expected score count
        const { data: teamData, error: teamError } = await supabase
          .from("teams")
          .select(
            `
            id,
            squadID,
            eventsID,
            squads!inner(tournamentID),
            tournamentEvents!inner(gamesPerEvent),
            teamassignments!inner(id)
          `
          )
          .eq("id", teamId)
          .single();

        if (teamError || !teamData) return false;

        const expectedScoreCount =
          teamData.tournamentEvents.gamesPerEvent *
          teamData.teamassignments.length;

        // Get actual score count
        const { count: actualScoreCount, error: scoreError } = await supabase
          .from("scores")
          .select("*", { count: "exact", head: true })
          .eq("teamID", teamId);

        if (scoreError) return false;

        return actualScoreCount >= expectedScoreCount;
      } catch (error) {
        console.error("Error checking team scores:", error);
        return false;
      }
    },
    [supabase]
  );

  // Synchronous version for UI state - this will be updated when scores change
  const canVerifyTeamForEdit = useCallback(
    (teamId) => canVerifyTeamScores(verificationStatus, teamId),
    [verificationStatus]
  );

  const canVerifyBowler = useCallback(
    (bowlerId) => {
      if (!verificationStatus || !verificationStatus.averages) return false;

      const bowlerVerified = verificationStatus.averages.find(
        (a) => a.entityId === bowlerId
      )?.isVerified;
      return !bowlerVerified;
    },
    [verificationStatus]
  );

  const canUnverifyBowler = useCallback(
    (bowlerId) => {
      if (!verificationStatus || !verificationStatus.averages) return false;

      const bowlerVerification = verificationStatus.averages.find(
        (a) => a.entityId === bowlerId
      );

      if (!bowlerVerification || !bowlerVerification.isVerified) return false;

      // Check if any scores depend on this bowler's average and are verified
      // If no scores are verified, the bowler can be unverified
      const scoresDependOnAverages =
        verificationStatus.unverificationPaths?.averages || [];
      return scoresDependOnAverages.length === 0;
    },
    [verificationStatus]
  );

  const getTeamVerificationStatus = useCallback(
    (teamId) => {
      if (!verificationStatus || !verificationStatus.scores) return null;
      return verificationStatus.scores.find((s) => s.entityId === teamId);
    },
    [verificationStatus]
  );

  const getBowlerVerificationStatus = useCallback(
    (bowlerId) => {
      if (!verificationStatus || !verificationStatus.averages) return null;
      return verificationStatus.averages.find((a) => a.entityId === bowlerId);
    },
    [verificationStatus]
  );

  return {
    verificationStatus,
    loading,
    error,
    refetch: fetchVerificationStatus,

    // Helper functions
    canVerifyTeam,
    canVerifyBowler,
    canUnverifyBowler,
    getTeamVerificationStatus,
    getBowlerVerificationStatus,
    teamHasAllScores,
    canVerifyTeamForEdit,

    // Computed properties
    canVerifyPayouts: verificationStatus?.computed?.canVerifyPayouts || false,
    canVerifyResults: verificationStatus?.computed?.canVerifyResults || false,
    canVerifyTournament:
      verificationStatus?.computed?.canVerifyTournament || false,

    // Unverification paths
    unverificationPaths: verificationStatus?.unverificationPaths || {},
  };
};
